import React, { useState, useEffect, useMemo } from "react";
import "./Quiz.css";
import { useLocation, useNavigate } from "react-router-dom";

function Quiz() {
  const location = useLocation() ?? {};
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [numberOfDemographicQuestions, setNumberOfDemographicQuestions] =
    useState(0);

  const quizType = location.state.type;

  // Fetch quiz data from the backend
  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_BACKEND_FLASK_API_URL}/quiz?type=${quizType}`,
      {
        method: "GET",
        credentials: "include",
      }
    )
      .then((response) => response.json())
      .then((data) => {
        setQuizQuestions(data[0]);
        setNumberOfDemographicQuestions(data[1]);
      })
      .catch((error) => console.error("Error fetching data:", error)); // Log any errors
  }, [quizType]);

  const answerOptions = useMemo(
    () => [
      "Does not describe me at all",
      "Barely describes me",
      "Somewhat describes me",
      "Moderately describes me",
      "Generally describes me",
      "Mostly describes me",
      "Completely describes me",
    ],
    []
  );
  const scores = {
    // Map for Likert scale scores
    "Does not describe me at all": 1,
    "Barely describes me": 2,
    "Somewhat describes me": 3,
    "Moderately describes me": 4,
    "Generally describes me": 5,
    "Mostly describes me": 6,
    "Completely describes me": 7,
  };

  // Navigation for buttons
  const navigate = useNavigate();

  const quizData = React.useMemo(() => {
    return quizQuestions.map((q, i) => ({
      question: q[0],
      scoreType: q[1],
      questionClass: q[2],
      options:
        location.state.type === "guest" && i < 12 ? q[2] ?? [] : answerOptions,
      questionType: q[3] ?? "",
      recommendations: q[4] ?? [],
    }));
  }, [quizQuestions, location.state.type, answerOptions]);
  console.log("Quiz Questions:", quizQuestions.length);
  console.log("Quiz Data Built:", quizData.length);

  const questionsPerPage = 22;
  const totalPages = Math.ceil(quizData.length / questionsPerPage);
  const [currentPage, setCurrentPage] = useState(0); // Tracks the current page (0-based)
  const [quizAnswers, setQuizAnswers] = useState({}); // Stores user's answers

  // Get the questions for the current page
  const currentQuestions = quizData.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  // Handles answer selection
  const handleAnswerChange = (questionIndex, selectedOption) => {
    setQuizAnswers({
      ...quizAnswers,
      [questionIndex]: selectedOption,
    });
  };

  // Check if all questions on the current page are answered
  const allQuestionsAnswered = currentQuestions.every(
    (_, index) =>
      quizAnswers[currentPage * questionsPerPage + index] !== undefined
  );

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevPage = () => {
    setCurrentPage(currentPage - 1);
  };

  const getNormalisedScore = (scoreType, answerScore) => {
    return scoreType === "negative"
      ? Object.keys(scores).length - answerScore + 1
      : answerScore;
  };

  // Calculate the score based on quizAnswers and the type of each question (positive/negative)
  const calculateScore = () => {
    if (quizType === "guest" || quizType === "firstquiz") {
      let userCogScore = 0;
      let userEmpScore = 0;
      let userAffectScore = 0;
      let pracCogScore = 0;
      let pracEmpScore = 0;
      let pracAffectScore = 0;

      let maxUserCogScore = 0;
      let maxUserEmpScore = 0;
      let maxUserAffectScore = 0;
      let maxPracCogScore = 0;
      let maxPracEmpScore = 0;
      let maxPracAffectScore = 0;

      Object.keys(quizAnswers).forEach((questionIndex) => {
        const answer = quizAnswers[questionIndex];
        const { scoreType, questionClass, questionType } =
          quizData[questionIndex];
        const answerScore = scores[answer];
        const normalisedAnswerScore = getNormalisedScore(
          scoreType,
          answerScore
        );

        if (questionType === "prac") {
          switch (questionClass) {
            case "Cognitive":
              pracCogScore += normalisedAnswerScore;
              maxPracCogScore += Object.keys(scores).length;
              break;
            case "Empathic Response":
              pracEmpScore += normalisedAnswerScore;
              maxPracEmpScore += Object.keys(scores).length;
              break;
            default:
              pracAffectScore += normalisedAnswerScore;
              maxPracAffectScore += Object.keys(scores).length;
          }
        } else if (questionType === "user") {
          switch (questionClass) {
            case "Cognitive":
              userCogScore += normalisedAnswerScore;
              maxUserCogScore += Object.keys(scores).length;
              break;
            case "Empathic Response":
              userEmpScore += normalisedAnswerScore;
              maxUserEmpScore += Object.keys(scores).length;
              break;
            default:
              userAffectScore += normalisedAnswerScore;
              maxUserAffectScore += Object.keys(scores).length;
          }
        }
      });
      return [
        [
          [pracCogScore, maxPracCogScore],
          [pracAffectScore, maxPracAffectScore],
          [pracEmpScore, maxPracEmpScore],
        ],
        [
          [userCogScore, maxUserCogScore],
          [userAffectScore, maxUserAffectScore],
          [userEmpScore, maxUserEmpScore],
        ],
      ];
    } else {
      let cogScore = 0;
      let empScore = 0;
      let affectScore = 0;

      let maxCogScore = 0;
      let maxEmpScore = 0;
      let maxAffectScore = 0;

      console.log("Quiz Answers:", quizAnswers);

      Object.keys(quizAnswers).forEach((questionIndex) => {
        const answer = quizAnswers[questionIndex];
        const { scoreType, questionClass } = quizData[questionIndex];
        const answerScore = scores[answer];
        const normalisedAnswerScore = getNormalisedScore(
          scoreType,
          answerScore
        );

        switch (questionClass) {
          case "Cognitive":
            cogScore += normalisedAnswerScore;
            maxCogScore += Object.keys(scores).length;
            break;
          case "Empathic Response":
            empScore += normalisedAnswerScore;
            maxEmpScore += Object.keys(scores).length;
            break;
          default:
            affectScore += normalisedAnswerScore;
            maxAffectScore += Object.keys(scores).length;
        }
      });

      return [
        [cogScore, maxCogScore],
        [affectScore, maxAffectScore],
        [empScore, maxEmpScore],
      ];
    }
  };

  const calculateRecommendations = () => {
    const allRecommendations = new Set();

    Object.keys(quizAnswers).forEach((questionIndex) => {
      const answer = quizAnswers[questionIndex];
      const { scoreType, recommendations } = quizData[questionIndex];
      const answerScore = scores[answer];
      const normalisedAnswerScore = getNormalisedScore(scoreType, answerScore);

      if (normalisedAnswerScore < 5) {
        recommendations.forEach((rec) => allRecommendations.add(rec));
      }
    });
    return Array.from(allRecommendations);
  };

  const handleSubmit = async () => {
    try {
      const totalScores = calculateScore();
      const recommendations = calculateRecommendations();

      let demographicAnswers = [];

      for (let i = 0; i < numberOfDemographicQuestions; i++) {
        demographicAnswers.push(quizAnswers[i]);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_FLASK_API_URL}/score`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            score: totalScores,
            quizType: `${location.state.type}`,
            demographicAnswers: demographicAnswers,
            recommendations: recommendations,
          }), // Send the score to the backend
        }
      );

      const data = await response.json();
      if (response.ok) {
        console.log("Score stored successfully:", data);
        navigate(`/feedback`); // Navigate to feedback after successful submission
      } else {
        console.error("Error storing score:", data.error);
      }
    } catch (error) {
      console.error("Error submitting quiz score:", error);
    }
  };

  // handling the logout button
  const handleLogout = () => {
    fetch(`${process.env.REACT_APP_BACKEND_FLASK_API_URL}/logout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data.message);
        navigate(`/`);
      })
      .catch((error) => console.error("Error logging out:", error));
  };

  return (
    <div className="quiz-container">
      {quizType !== "guest" && (
        <header className="dash-header">
          <button onClick={() => navigate(`/dashboard`)}>Dashboard</button>
          <button onClick={handleLogout}>Log Out</button>
        </header>
      )}

      <h2 style={{ textAlign: "center" }}>
        {quizType === "guest"
          ? "Guest Empathy Quiz"
          : quizType === "firstquiz"
          ? "Empathy Quiz"
          : `Practitioner-to-${quizType} Empathy Quiz`}
      </h2>
      <h4 style={{ textAlign: "center" }}>
        {quizType === "guest" && currentPage === 0
          ? "All questions need to be answered. Please type N/A if a textbox question is not applicable"
          : "All questions need to be answered."}
      </h4>

      {currentQuestions.map((question, index) => {
        const globalIndex = currentPage * questionsPerPage + index;
        return (
          <div key={globalIndex} className="question-container">
            <h3>
              {globalIndex + 1}. {question.question}
            </h3>
            {Array.isArray(question.options) && question.options.length > 0 ? ( // Render multiple-choice buttons if options exist
              <div className="likert-scale">
                {question.options.map((option, optionIndex) => (
                  <button
                    key={optionIndex}
                    className={
                      quizAnswers[globalIndex] === option ? "selected" : ""
                    }
                    onClick={() => handleAnswerChange(globalIndex, option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            ) : (
              // Render a textbox if there are no mult choice options
              <textarea
                className="text-input"
                placeholder="Enter your response"
                value={quizAnswers[globalIndex] || ""}
                onChange={(e) =>
                  handleAnswerChange(globalIndex, e.target.value)
                }
                rows={4}
                cols={50}
              />
            )}
          </div>
        );
      })}

      {currentPage >= 1 && (
        <button className="back-btn" onClick={handlePrevPage}>
          Back
        </button>
      )}

      {currentPage === totalPages - 1 ? (
        <button
          className="submit-btn"
          onClick={handleSubmit}
          disabled={!allQuestionsAnswered}
        >
          Submit
        </button>
      ) : (
        <button
          className="submit-btn"
          onClick={handleNextPage}
          disabled={!allQuestionsAnswered}
        >
          Next
        </button>
      )}
    </div>
  );
}

export default Quiz;
